import { useEffect, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { useTooltip } from '@/hooks/useTooltip';

/**
 * Приводит цвет к строчному hex: из HTML цвет может прийти как `rgb(r, g, b)`
 * (браузер нормализует style-атрибут), а пресеты заданы в hex — без этого
 * подсветка текущего цвета в палитре не срабатывала бы на сохранённых
 * шаблонах.
 */
function toHex(color: string | undefined): string | null {
  if (!color) return null;
  const rgb = color.match(/^rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
  if (rgb) {
    const hex = (n: string) => Number(n).toString(16).padStart(2, '0');
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
  }
  const short = color.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (short) return `#${short[1]}${short[1]}${short[2]}${short[2]}${short[3]}${short[3]}`.toLowerCase();
  return color.toLowerCase();
}

/** Палитра по умолчанию — тот же набор, что предлагал Quill в старой админке. */
const PRESETS = [
  '#000000', '#444444', '#888888', '#bbbbbb', '#ffffff',
  '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc',
  '#9933ff', '#f06666', '#ffc266', '#66b966', '#66a3e0',
];

/**
 * Цвет текста / цвет выделения — попап с готовой палитрой и сбросом,
 * вместо нативного <input type="color"> (тот не давал ни выбора из
 * дефолтных цветов, ни возможности снять уже применённый цвет). Свой
 * произвольный цвет по-прежнему доступен — пункт «Свой цвет…» открывает
 * нативную палитру ОС.
 */
export function RichTextColorButton({
  editor,
  disabled,
  mode,
}: {
  editor: Editor;
  disabled: boolean;
  mode: 'text' | 'highlight';
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const nativeRef = useRef<HTMLInputElement>(null);

  const title = mode === 'text' ? 'Цвет текста' : 'Цвет выделения';
  const resetTooltip = useTooltip<HTMLButtonElement>('Сбросить цвет');
  const triggerTooltip = useTooltip<HTMLButtonElement>(title);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  /**
   * keepOpen — для «Свой цвет…»: нативная палитра ОС шлёт change на каждое
   * движение по спектру, и закрытие попапа на первом же событии выбивало
   * пользователя из подбора цвета. Попап закроется сам по крестику или
   * клику мимо. Выбор готового цвета из сетки — наоборот, разовое действие,
   * там закрываем сразу.
   */
  const apply = (color: string, keepOpen = false) => {
    if (mode === 'text') editor.chain().focus().setColor(color).run();
    else editor.chain().focus().setHighlight({ color }).run();
    if (!keepOpen) setOpen(false);
  };

  const reset = () => {
    if (mode === 'text') editor.chain().focus().unsetColor().run();
    else editor.chain().focus().unsetHighlight().run();
    setOpen(false);
  };

  const active = mode === 'text' ? editor.isActive('textStyle') : editor.isActive('highlight');
  const currentColor = mode === 'text'
    ? (editor.getAttributes('textStyle').color as string | undefined)
    : (editor.getAttributes('highlight').color as string | undefined);
  const currentHex = toHex(currentColor);

  return (
    <div className="rich-text-editor__color" ref={wrapRef}>
      <button
        ref={triggerTooltip.ref}
        type="button"
        aria-label={title}
        aria-pressed={active}
        disabled={disabled}
        className={`rich-text-editor__btn${active ? ' rich-text-editor__btn--active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
        {...triggerTooltip.handlers}
      >
        {triggerTooltip.node}
        <span className={mode === 'highlight' ? 'rich-text-editor__highlight-swatch' : undefined}>A</span>
        <span className="rich-text-editor__color-bar" style={{ background: currentColor ?? 'transparent' }} />
      </button>

      {open && (
        <div className="rich-text-editor__color-popover">
          <div className="rich-text-editor__color-grid">
            {PRESETS.map((color) => {
              const selected = currentHex === color.toLowerCase();
              return (
                <button
                  key={color}
                  type="button"
                  title={color}
                  aria-label={color}
                  aria-pressed={selected}
                  className={`rich-text-editor__color-swatch${selected ? ' rich-text-editor__color-swatch--selected' : ''}`}
                  style={{ background: color }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => apply(color)}
                />
              );
            })}
          </div>

          <div className="rich-text-editor__color-actions">
            {/* Нативную палитру ОС открывает сам <input type="color">, лежащий
                прозрачным слоем поверх кнопки, а не JS-клик по hidden-инпуту:
                у hidden-элемента нет позиции на экране, и браузер показывал
                панель выбора цвета в углу окна вместо места клика. */}
            <span className="rich-text-editor__color-native btn btn--secondary btn--sm">
              Свой цвет…
              <input
                ref={nativeRef}
                type="color"
                aria-label={`${title}: свой цвет`}
                defaultValue={currentHex ?? '#000000'}
                onChange={(e) => apply(e.target.value, true)}
              />
            </span>
            {/* Крестик = сброс цвета (и закрытие попапа): отдельная кнопка
                «Сбросить» рядом с ним читалась как дубль одного действия. */}
            <button
              ref={resetTooltip.ref}
              type="button"
              className="rich-text-editor__color-close"
              aria-label="Сбросить цвет"
              onMouseDown={(e) => e.preventDefault()}
              onClick={reset}
              {...resetTooltip.handlers}
            >
              ×
              {resetTooltip.node}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
